import type { FC, ReactNode, Dispatch, SetStateAction } from 'react'
import { createContext, useState, useEffect } from 'react'
import type { TypeUserState } from '@/schema'
import { cookie, DY_COOKIE_SESSION, getUserDataFromCookie } from '@/utils'
import { usePathname, useRouter } from 'next/navigation'
import { checkIfPublicPath, checkIfLegalPath } from '@/proxy'
import { fetchAPI } from '@/api'
import { getMaxAge } from '@/components/forms/FormAuthLogin'

type TypeState = {
  user: TypeUserState
  setUser: Dispatch<SetStateAction<TypeUserState>>
}

const initialState: TypeState = {
  user: null,
  setUser: () => { },
}

export const SessionContext = createContext<TypeState | null>(initialState)

interface Props {
  children: ReactNode
}

export const SessionProvider: FC<Props> = ({ children }) => {
  const [user, setUser] = useState<TypeUserState>(undefined)
  const { push } = useRouter()
  const pathname = usePathname()

  useEffect(() => {
    const userCookieData = getUserDataFromCookie(cookie.get(DY_COOKIE_SESSION))
    const controller = new AbortController()

    const getLatestUserData = async () => {
      const res = await fetchAPI('user-account', null, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userCookieData?.token}`,
        },
        signal: controller.signal,
      }) as Response

      if (!res.ok) {
        console.warn('SessionProvider - getLatestUserData - res not ok', res)
        return
      }

      const data = await res.json()

      const vets_ids = data.vets?.map(vet => vet?.id)
      const vetsLength = data.vets?.length

      localStorage.setItem(`dy_virbac_user_${data.id}`, JSON.stringify(data?.vets))
      setUser({
        ...data,
        token: userCookieData.token,
        vets: data.vets,
        vets_ids
      })

      const incomingCookie = { ...userCookieData, user: { ...userCookieData.user } }
      incomingCookie.user.vet = data.vets?.[0]
      incomingCookie.user.vetsLength = vetsLength
      incomingCookie.user.vets_ids = vets_ids

      cookie.set(DY_COOKIE_SESSION, JSON.stringify(incomingCookie), { maxAge: getMaxAge(userCookieData.exp) })
    }

    if (!userCookieData) {
      if (!checkIfPublicPath(pathname) && !checkIfLegalPath(pathname)) push('/login')
    } else {
      getLatestUserData()
    }

    return () => controller.abort()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  return <SessionContext.Provider value={{ user, setUser }}>{children}</SessionContext.Provider>
}
